/**
 * Active filters context.
 *
 * Any page can call `useRegisterFilters([{label, value, onClear}])` to surface
 * its current filter state as dismissable pills in the global TopBar.
 * The hook only registers while the page is mounted and unregisters on unmount,
 * so the pill strip is always context-accurate.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ActiveFiltersContext = createContext(null);

export const ActiveFiltersProvider = ({ children }) => {
  // Map<pageKey, filters[]> — one page at a time typically, but keyed to avoid
  // flicker if two pages are transiently mounted during navigation.
  const [pages, setPages] = useState({});

  const setFiltersForPage = useCallback((key, filters) => {
    setPages((prev) => {
      if (!filters || filters.length === 0) {
        if (!(key in prev)) return prev;
        const { [key]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: filters };
    });
  }, []);

  const removePage = useCallback((key) => {
    setPages((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  const activeFilters = useMemo(
    () => Object.values(pages).flat(),
    [pages]
  );

  const value = useMemo(
    () => ({ activeFilters, setFiltersForPage, removePage }),
    [activeFilters, setFiltersForPage, removePage]
  );

  return (
    <ActiveFiltersContext.Provider value={value}>
      {children}
    </ActiveFiltersContext.Provider>
  );
};

export const useActiveFilters = () => {
  const ctx = useContext(ActiveFiltersContext);
  if (!ctx) {
    // Safe fallback so pages using the hook outside the provider don't crash.
    return { activeFilters: [], setFiltersForPage: () => {}, removePage: () => {} };
  }
  return ctx;
};

/**
 * Register the current page's active filters.
 *
 * @param {string} pageKey   Stable identifier for the page (e.g. "work-orders").
 * @param {Array<{label,value,onClear}>} filters  Dismissable chips.
 */
export const useRegisterFilters = (pageKey, filters) => {
  const { setFiltersForPage, removePage } = useActiveFilters();

  useEffect(() => {
    setFiltersForPage(pageKey, filters);
    return () => removePage(pageKey);
    // JSON-stringified filters as the dep so callers don't have to memoise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, JSON.stringify(filters?.map((f) => ({ l: f.label, v: f.value })))]);
};

export default ActiveFiltersContext;
